import React, { useCallback, useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { get } from 'lodash';
import {
  ATMButton,
  ATMField,
  ATMForm,
  ATMHeader,
  ATMGrid,
  ATMTable,
  ATMInput,
  ATMSelect,
} from 'shared-it-appmod-ui';
import Confirm from 'src/components/atoms/confirm/confirm.component';
import DropdownEmployee from 'src/components/atoms/dropdown/dropdown-employee/dropdown-employee.component';
import { DEFAULT_EMPLOYEE_NUMBER, PassengerType } from 'src/constants';
import Lang from 'src/libraries/language';
import { IRequestFormMethods } from 'src/models/request.model';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import { ToastSuccess } from 'src/components/atoms/toaster/toaster.component';

type IProps = IRequestFormMethods & {
  name: string;
  limit?: number;
};

const FlightRequestPassengerInformationForm: React.FC<IProps> = ({
  control,
  errors,
  name,
  limit = 4,
}) => {
  const [deleteEmployee, setDeleteEmployee] = useState<string>();
  const [passengers, setPassengers] = useState<Record<string, string>>({});
  const [passengerType, setPassengerType] = useState<
    Record<string, PassengerType>
  >({});

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const handleAdd = useCallback(() => {
    append({
      empNo: undefined,
      name: undefined,
      weight: undefined,
    });
  }, [append]);

  useEffect(() => {
    if (!fields || (fields && !fields.length)) {
      handleAdd();
    }
  }, [fields, handleAdd]);

  useEffect(() => {
    fields.forEach((value) => {
      setPassengerType((values) => {
        return {
          ...values,
          [value.id]:
            value.empNo === DEFAULT_EMPLOYEE_NUMBER
              ? PassengerType.Contractor
              : PassengerType.Employee,
        };
      });

      if (value.passengerId) {
        setPassengers((values) => {
          return {
            ...values,
            [value.id]: value.name,
          };
        });
      }
    });
  }, [fields, setPassengerType]);

  const handleType = useCallback(
    (index, value) => {
      setPassengerType((values) => ({
        ...values,
        [index]: value,
      }));
    },
    [setPassengerType]
  );

  const handleEmployee = useCallback(
    (elementId, index, employee) => {
      const fullname = `${employee.first_name} ${employee.last_name}`;

      setPassengers((values) => ({
        ...values,
        [elementId]: fullname,
      }));

      control.setValue(`${name}[${index}].name`, fullname);
    },
    [name, setPassengers]
  );

  return (
    <>
      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_PASSENGER_INFORMATION}
      </ATMHeader>

      <ATMGrid>
        <ATMGrid.Column>
          <ATMTable>
            <ATMTable.Header>
              <ATMTable.Row>
                <ATMTable.HeaderCell width={5}>
                  {Lang.LBL_PASSENGER_TYPE}
                </ATMTable.HeaderCell>
                <ATMTable.HeaderCell width={5}>
                  <LabelRequired>{Lang.LBL_PASSENGER_NAME}</LabelRequired>
                </ATMTable.HeaderCell>
                <ATMTable.HeaderCell width={5}>
                  <LabelRequired>{Lang.LBL_WEIGHT}</LabelRequired>
                </ATMTable.HeaderCell>
                <ATMTable.HeaderCell width={1} />
              </ATMTable.Row>
            </ATMTable.Header>

            <ATMTable.Body>
              {fields.map((item, i) => {
                const elementId = item.id ?? '';

                return (
                  <ATMTable.Row key={elementId}>
                    <ATMTable.Cell>
                      <ATMForm.Field
                        placeholder={Lang.LBL_SELECT}
                        control={ATMSelect}
                        options={Object.entries(PassengerType).map(
                          ([_, value]) => ({
                            key: value,
                            value,
                            text: value,
                          })
                        )}
                        value={
                          passengerType[elementId] ?? PassengerType.Employee
                        }
                        onChange={(_, { value }) =>
                          handleType(elementId, value)
                        }
                      />
                    </ATMTable.Cell>
                    <ATMTable.Cell>
                      {passengerType[elementId] === PassengerType.Contractor ? (
                        <>
                          <ATMField
                            key={`emp_${PassengerType.Contractor}_${elementId}`}
                            name={`${name}[${i}].empNo`}
                            control={control}
                            as={ATMInput}
                            defaultValue="0"
                            type="hidden"
                            className="hidden"
                          />
                          <ATMField
                            key={`name_${PassengerType.Contractor}_${elementId}`}
                            name={`${name}[${i}].name`}
                            control={control}
                            as={ATMInput}
                            defaultValue={item.name}
                            placeholder={Lang.LBL_ENTER}
                            onChange={([_, { value }]) => {
                              setPassengers((values) => ({
                                ...values,
                                [elementId]: value,
                              }));

                              return value;
                            }}
                            error={get(errors, `${name}[${i}].name`, false)}
                          />
                        </>
                      ) : (
                        <>
                          <ATMField
                            key={`name_${PassengerType.Employee}_${elementId}`}
                            name={`${name}[${i}].name`}
                            control={control}
                            as={ATMInput}
                            defaultValue={item.name}
                            type="hidden"
                            className="hidden"
                          />
                          <ATMField
                            key={`emp_${PassengerType.Employee}_${elementId}`}
                            name={`${name}[${i}].empNo`}
                            control={control}
                            as={DropdownEmployee}
                            defaultValue={
                              item.empNo !== DEFAULT_EMPLOYEE_NUMBER
                                ? item.empNo
                                : undefined
                            }
                            onChange={([_, { value }, employee]: any) => {
                              if (employee) {
                                handleEmployee(elementId, i, employee);
                              }

                              return value;
                            }}
                            onLoad={(employee) => {
                              // The purpose of this one, is just to get the fullname when this dropdown component mount
                              if (employee) {
                                handleEmployee(elementId, i, employee);
                              }
                            }}
                            icon="search"
                            clearable
                            error={get(errors, `${name}[${i}].empNo`, false)}
                          />
                        </>
                      )}
                    </ATMTable.Cell>
                    <ATMTable.Cell>
                      <ATMField
                        key={`weight_${elementId}`}
                        name={`${name}[${i}].weight`}
                        as={ATMInput}
                        type="number"
                        control={control}
                        placeholder={Lang.LBL_ENTER}
                        defaultValue={item.weight}
                        error={get(errors, `${name}[${i}].weight`, false)}
                      />
                    </ATMTable.Cell>

                    <ATMTable.Cell textAlign="center">
                      {fields.length > 1 && (
                        <ATMButton
                          icon="trash alternate outline"
                          type="button"
                          onClick={() =>
                            item.passengerId
                              ? setDeleteEmployee(elementId)
                              : remove(i)
                          }
                        />
                      )}
                    </ATMTable.Cell>
                  </ATMTable.Row>
                );
              })}
            </ATMTable.Body>
            <ATMTable.Footer>
              <ATMTable.Row>
                <ATMTable.HeaderCell colSpan={4}>
                  <ATMButton
                    secondary
                    icon="plus"
                    type="button"
                    onClick={handleAdd}
                    disabled={fields.length >= limit}
                    content={Lang.LBL_ADD}
                  />
                </ATMTable.HeaderCell>
              </ATMTable.Row>
            </ATMTable.Footer>
          </ATMTable>
          <Confirm
            open={deleteEmployee !== undefined}
            size="tiny"
            onCancel={() => setDeleteEmployee(undefined)}
            header={Lang.TTL_CONFIRM_DELETE}
            content={Lang.formatString(
              Lang.MSG_CONFIRM_DELETE,
              passengers[deleteEmployee ?? 0] ?? ''
            )}
            onConfirm={() => {
              remove(fields.findIndex((value) => value.id === deleteEmployee));
              ToastSuccess(
                Lang.formatString(
                  Lang.MSG_PASSENGER_DELETE_SUCCESS,
                  passengers[deleteEmployee ?? 0] ?? ''
                )
              );
              setDeleteEmployee(undefined);
            }}
            confirmText={Lang.LBL_YES_DELETE}
          />
        </ATMGrid.Column>
      </ATMGrid>
    </>
  );
};

export default FlightRequestPassengerInformationForm;
